import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/use-mobile';
import { EDITOR_DESKTOP_MEDIA_QUERY } from '@/components/app-editor/use-editor-chrome';
import type { MockupFocus } from '@/pages/onboarding/mobile-studio-geometry';
import { OnboardingAccountActions } from '@/pages/onboarding/onboarding-account-actions';
import {
  OnboardingMobileStudio,
  useOnboardingFooterPlacement,
} from '@/pages/onboarding/onboarding-mobile-studio';
import {
  KorzaStudioBackdrop,
  KorzaStudioBadge,
} from '@/pages/onboarding/mockup/korza-studio-backdrop';

// Two-column layout for the onboarding-with-mockup flow: form content on the
// left, iPhone mockup on the right. Mirrors the structure of
// SignupPageLayout/SignupPageHeader/SignupPageContent/SignupPageFooter
// (`@/components/signup`) but as a dedicated set for the two-column shape —
// the signup versions are full-width/single-column and not reusable as-is.
// Below `lg` the mockup moves on top of the form (OnboardingMobileStudio),
// unless the step opts out with `mobileMockup={false}`.

export interface OnboardingPageLayoutProps {
  /** Left column content (header + form content + footer). */
  children: ReactNode;
  /** Right column content (the IPhoneMockup + current scene). */
  mockup: ReactNode;
  /**
   * Show the mockup below `lg` too (default). Off for a step whose form must
   * be fully visible on the first screen.
   */
  mobileMockup?: boolean;
  /**
   * Below `lg`, what the Studio frames (the desktop's ZoomedIPhoneMockup zoom
   * does not apply there); the whole phone when absent.
   */
  mobileFocus?: MockupFocus;
  /** Below `lg`, open the sheet only as tall as the form. */
  mobileFitSheet?: boolean;
  className?: string;
}

export function OnboardingPageLayout({
  children,
  mockup,
  mobileMockup = true,
  mobileFocus,
  mobileFitSheet,
  className,
}: OnboardingPageLayoutProps) {
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);

  if (!isDesktop && mobileMockup) {
    return (
      <OnboardingMobileStudio
        mockup={mockup}
        focus={mobileFocus}
        fitSheet={mobileFitSheet}
        className={className}
      >
        {children}
      </OnboardingMobileStudio>
    );
  }

  return (
    <div
      className={cn('flex min-h-screen w-full flex-col lg:flex-row', className)}
    >
      <div className="relative flex flex-1 flex-col items-center justify-center p-6 pb-24 pt-16 lg:p-12">
        <OnboardingAccountActions className="absolute left-4 top-4" />
        <div className="flex w-full max-w-md flex-col">{children}</div>
      </div>
      <div className="relative hidden flex-1 items-center justify-center overflow-hidden p-6 lg:sticky lg:top-0 lg:flex lg:h-screen lg:border-l lg:border-border/60 lg:p-12">
        <KorzaStudioBackdrop />
        {/* Zoomed mockups (ZoomedIPhoneMockup) intentionally render taller
            than this column — clipped here instead of the column scrolling
            or the phone spilling over the form column beside it. */}
        <div className="relative z-10">{mockup}</div>
        <KorzaStudioBadge />
      </div>
    </div>
  );
}

export interface OnboardingPageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  className?: string;
}

export function OnboardingPageHeader({
  title,
  subtitle,
  className,
}: OnboardingPageHeaderProps) {
  return (
    <div className={cn('mb-6 max-lg:mb-4', className)}>
      <h1 className="mb-2 text-3xl font-medium max-lg:text-2xl">{title}</h1>
      {subtitle && (
        <p className="text-balance text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
}

export interface OnboardingPageContentProps {
  children: ReactNode;
  className?: string;
}

export function OnboardingPageContent({
  children,
  className,
}: OnboardingPageContentProps) {
  return <div className={cn('flex flex-col gap-4', className)}>{children}</div>;
}

export interface OnboardingPageFooterProps {
  children: ReactNode;
  className?: string;
}

export function OnboardingPageFooter({
  children,
  className,
}: OnboardingPageFooterProps) {
  // In the mobile Studio's sheet the buttons stay at its bottom edge, at any
  // height, except while the keyboard is open (they'd cover the field).
  const placement = useOnboardingFooterPlacement();
  return (
    <div
      data-placement={placement ?? undefined}
      className={cn(
        'mt-8 flex justify-end',
        placement === 'pinned' &&
          'sticky bottom-0 z-10 -mx-5 mt-6 border-t bg-background px-5 py-3',
        placement === 'inline' && 'pb-4',
        className
      )}
    >
      {children}
    </div>
  );
}
